import { useEffect, useState, type ReactNode } from 'react'
import {
  ChevronDown,
  ChevronUp,
  MapPinOff,
  Mountain,
  Music,
  Plus,
  Tent,
  Trash2,
  Undo2,
} from 'lucide-react'

import { Field } from '../../components/Field'
import type { Camping, Stage } from '../../lib/types'
import { AddressSearch } from './AddressSearch'
import {
  entwurfAus,
  koordinatenSchluessel,
  pruefeEntwurf,
  type KoordinatenEntwurf,
} from './platzKoordinate'
import { useRowKeys } from './rowKeys'
import { TulipEditor } from './TulipEditor'
import { TulipVorschlaege } from './TulipVorschlaege'
import {
  appendItem,
  emptyCamping,
  insertAt,
  moveItem,
  patchAt,
  removeAt,
  replaceAt,
} from './stageLists'

/**
 * Die Inhalte einer Etappe bearbeiten: Schlafplätze, Highlights, Abendtipps
 * und der Hinweis.
 *
 * Wer unterwegs eine andere Route nimmt, muss nicht nur Start und Ziel
 * umschreiben können, sondern auch den Platz eintragen, auf dem er nun
 * wirklich steht.
 *
 * Gelöscht wird ohne Rückfrage, dafür lässt sich der zuletzt entfernte
 * Eintrag mit einem Griff zurueckholen: eine Rückfrage bei jedem Handgriff
 * wäre auf einem Telefon am Strassenrand nur lästig, ein versehentliches
 * Antippen darf aber trotzdem nichts endgültig kosten.
 */
export function StageContentEditor({
  stage,
  onChange,
}: {
  stage: Stage
  onChange: (patch: Partial<Stage>) => void
}) {
  return (
    <div className="inhalt-bearbeiten kein-druck">
      <CampingEditor werte={stage.camping} onChange={(camping) => onChange({ camping })} />

      <TextListEditor
        titel="Unterwegs"
        einzahl="Highlight"
        symbol={<Mountain size={13} />}
        platzhalter="z. B. Vršič-Pass, Baden im Fluss"
        leerText="Noch nichts eingetragen."
        werte={stage.highlights}
        onChange={(highlights) => onChange({ highlights })}
      />

      {/*
        Das Roadbook steht zwischen dem, was unterwegs kommt, und dem, was
        abends kommt - genau dort, wo es hingehört: es beschreibt die Fahrt.

        Der Vorschlag steht darüber und nicht darunter: er ist der Anfang der
        Arbeit und nicht ihr Nachtrag. Wer eine Etappe zum ersten Mal öffnet,
        soll den Knopf sehen, bevor er die leere Liste sieht - genau daran ist
        die Notation bisher gescheitert.
      */}
      <TulipVorschlaege stage={stage} onChange={onChange} />
      <TulipEditor stage={stage} onChange={onChange} />

      <TextListEditor
        titel="Abends"
        einzahl="Abendtipp"
        symbol={<Music size={13} />}
        platzhalter="z. B. Konoba am Hafen"
        leerText="Noch nichts eingetragen."
        werte={stage.night}
        onChange={(night) => onChange({ night })}
      />

      <div className="inhalt-block">
        <Field label="Hinweis">
          <textarea
            rows={3}
            value={stage.tip}
            placeholder="Was man an diesem Tag wissen sollte: Grenze, Tanken, Sperrung"
            onChange={(e) => onChange({ tip: e.target.value })}
          />
        </Field>
      </div>
    </div>
  )
}

/** Schlafplätze: drei Felder je Eintrag, Reihenfolge ist die Rangfolge. */
function CampingEditor({
  werte,
  onChange,
}: {
  werte: Camping[]
  onChange: (next: Camping[]) => void
}) {
  const keys = useRowKeys(werte.length)
  const [zurueck, setZurueck] = useState<{ index: number; platz: Camping } | null>(null)

  function entfernen(index: number) {
    const platz = werte[index]
    if (!platz) return

    keys.remove(index)
    setZurueck({ index, platz })
    onChange(removeAt(werte, index))
  }

  function zurueckholen() {
    if (!zurueck) return

    keys.insert(zurueck.index)
    onChange(insertAt(werte, zurueck.index, zurueck.platz))
    setZurueck(null)
  }

  function anhaengen() {
    keys.append()
    // Nach dem Anhängen zeigt der gemerkte Index woandershin, also fallen
    // lassen. Dasselbe gilt beim Verschieben.
    setZurueck(null)
    onChange(appendItem(werte, emptyCamping()))
  }

  function verschieben(index: number, richtung: -1 | 1) {
    keys.move(index, richtung)
    setZurueck(null)
    onChange(moveItem(werte, index, richtung))
  }

  return (
    <section className="inhalt-block">
      <h5 className="block-titel">
        <Tent size={13} /> Schlafen
      </h5>

      {werte.length === 0 && <p className="leer">Noch kein Platz eingetragen.</p>}

      <ul className="eintragsliste">
        {werte.map((platz, i) => (
          <li key={keys.at(i)} className="eintrag">
            <span className="eintrag-nummer">Platz {i + 1}</span>

            <Field label="Name">
              <input
                value={platz.name}
                autoComplete="off"
                placeholder="Campingplatz oder Stellplatz"
                onChange={(e) => onChange(patchAt(werte, i, { name: e.target.value }))}
              />
            </Field>

            <div className="raster-2">
              <Field label="Preis">
                {/* Kein numerisches Feld: hier steht oft "18 €" oder "20 KM/Nacht". */}
                <input
                  value={platz.price}
                  autoComplete="off"
                  placeholder="z. B. 18 €"
                  onChange={(e) => onChange(patchAt(werte, i, { price: e.target.value }))}
                />
              </Field>
              <Field label="Notiz">
                <input
                  value={platz.note}
                  autoComplete="off"
                  placeholder="Lage, Strom, Dusche"
                  onChange={(e) => onChange(patchAt(werte, i, { note: e.target.value }))}
                />
              </Field>
            </div>

            <PlatzKoordinate
              platz={platz}
              onChange={(next) => onChange(replaceAt(werte, i, next))}
            />

            <div className="eintrag-knoepfe">
              <button
                type="button"
                className="knopf still"
                disabled={i === 0}
                onClick={() => verschieben(i, -1)}
                aria-label={`Platz ${i + 1} nach oben`}
              >
                <ChevronUp size={14} /> Höher
              </button>
              <button
                type="button"
                className="knopf still"
                disabled={i === werte.length - 1}
                onClick={() => verschieben(i, 1)}
                aria-label={`Platz ${i + 1} nach unten`}
              >
                <ChevronDown size={14} /> Tiefer
              </button>
              <button
                type="button"
                className="knopf gefahr"
                onClick={() => entfernen(i)}
                aria-label={`Platz ${i + 1} entfernen`}
              >
                <Trash2 size={14} /> Entfernen
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={anhaengen}>
          <Plus size={14} /> Platz hinzufügen
        </button>
        {zurueck && (
          <button type="button" className="knopf still" onClick={zurueckholen}>
            <Undo2 size={14} /> {beschriftung(zurueck.platz.name)} zurückholen
          </button>
        )}
      </div>
    </section>
  )
}

/**
 * Wo ein Platz liegt: Adresssuche und zwei Zahlenfelder.
 *
 * Die Koordinate ist freiwillig und bleibt es. Fehlt sie, sitzt der Platz im
 * Export für Navigationsgeräte weiterhin auf dem Etappenziel, und die
 * Beschreibung sagt das ausdrücklich - das ist für „Camps um Ilidža“ auch die
 * ehrlichere Auskunft. Steht sie, führt das Gerät abends zum Platz.
 *
 * Die Zahlenfelder führen einen eigenen Entwurf, der nur bei einer brauchbaren
 * Koordinate in die Reise geschrieben wird. Sonst stünde nach dem ersten
 * Tastendruck in „46.36“ die Breite 4 im Dokument, und wer ein Feld leert,
 * schriebe eine 0 - den Äquator. Beide Felder leer heisst „keine Koordinate“
 * und nimmt sie wieder heraus.
 */
function PlatzKoordinate({
  platz,
  onChange,
}: {
  platz: Camping
  onChange: (next: Camping) => void
}) {
  const gesetzt = koordinatenSchluessel(platz.at)
  const [entwurf, setEntwurf] = useState(() => entwurfAus(gesetzt))

  // Ändert sich die gespeicherte Koordinate von aussen - durch die Adresssuche,
  // durch einen Abgleich, durch das Zurückholen eines entfernten Platzes -,
  // folgen die Felder.
  useEffect(() => {
    setEntwurf(entwurfAus(gesetzt))
  }, [gesetzt])

  // Was in den Feldern steht, muss nicht dasselbe sein wie das, was gespeichert
  // ist - genau dann steht unten eine Meldung. Ohne sie sähe jemand eine 999 in
  // der Breite und glaubte, sie gelte.
  const befund = pruefeEntwurf(entwurf)

  const uebernehmen = (next: KoordinatenEntwurf) => {
    setEntwurf(next)
    const naechster = pruefeEntwurf(next)
    if (naechster.art === 'entfernen') onChange(ohneKoordinate(platz))
    if (naechster.art === 'setzen') onChange({ ...platz, at: naechster.at })
  }

  return (
    <div className="platz-ort">
      <AddressSearch
        platzhalter="Name des Platzes, Ort, Land"
        vorgabe={platz.name}
        onPick={(at) => {
          onChange({ ...platz, at })
        }}
      />

      <div className="raster-2">
        <Field label="Breite">
          <input
            type="number"
            inputMode="decimal"
            step={0.00001}
            value={entwurf.lat}
            placeholder="z. B. 46.361"
            onChange={(e) => uebernehmen({ ...entwurf, lat: e.target.value })}
          />
        </Field>
        <Field label="Länge">
          <input
            type="number"
            inputMode="decimal"
            step={0.00001}
            value={entwurf.lon}
            placeholder="z. B. 14.07793"
            onChange={(e) => uebernehmen({ ...entwurf, lon: e.target.value })}
          />
        </Field>
      </div>

      {befund.art === 'unvollstaendig' && (
        <p className="karte-warnung">
          Ein Feld ist leer. Übernommen wird erst, wenn beide eine Zahl tragen — oder beide leer
          sind, dann fällt die Koordinate weg.
        </p>
      )}
      {befund.art === 'unbrauchbar' && (
        <p className="karte-warnung">
          Diese beiden Zahlen ergeben keinen Ort auf der Erde. Übernommen ist{' '}
          {platz.at ? 'weiterhin die Koordinate von vorher' : 'nichts'}.
        </p>
      )}

      {platz.at ? (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf still"
            onClick={() => {
              onChange(ohneKoordinate(platz))
            }}
          >
            <MapPinOff size={14} /> Koordinate entfernen
          </button>
        </div>
      ) : (
        <p className="leer">
          Ohne Koordinate steht dieser Platz im Navigationsexport auf dem Etappenziel — mit dem
          Hinweis, dass das nicht seine Adresse ist.
        </p>
      )}
    </div>
  )
}

/**
 * Der Platz ohne Koordinate.
 *
 * Das Feld wird entfernt und nicht auf undefined gesetzt: ein Dokument mit
 * einem Feld, das da ist und nichts enthält, sieht nach dem Abgleich anders
 * aus als eines ohne das Feld - und ist doch dasselbe.
 */
function ohneKoordinate(platz: Camping): Camping {
  const rest = { ...platz }
  delete rest.at
  return rest
}

/** Eine Liste einzeiliger Texte: Highlights und Abendtipps teilen sich das. */
function TextListEditor({
  titel,
  einzahl,
  symbol,
  platzhalter,
  leerText,
  werte,
  onChange,
}: {
  titel: string
  einzahl: string
  symbol: ReactNode
  platzhalter: string
  leerText: string
  werte: string[]
  onChange: (next: string[]) => void
}) {
  const keys = useRowKeys(werte.length)
  const [zurueck, setZurueck] = useState<{ index: number; text: string } | null>(null)

  function entfernen(index: number) {
    const text = werte[index]
    if (text === undefined) return

    keys.remove(index)
    setZurueck({ index, text })
    onChange(removeAt(werte, index))
  }

  function zurueckholen() {
    if (!zurueck) return

    keys.insert(zurueck.index)
    onChange(insertAt(werte, zurueck.index, zurueck.text))
    setZurueck(null)
  }

  function anhaengen() {
    keys.append()
    setZurueck(null)
    onChange(appendItem(werte, ''))
  }

  return (
    <section className="inhalt-block">
      <h5 className="block-titel">
        {symbol} {titel}
      </h5>

      {werte.length === 0 && <p className="leer">{leerText}</p>}

      <ul className="eintragsliste">
        {werte.map((text, i) => (
          <li key={keys.at(i)} className="eintrag-zeile">
            <Field label={`${einzahl} ${i + 1}`}>
              <input
                value={text}
                autoComplete="off"
                placeholder={platzhalter}
                onChange={(e) => onChange(replaceAt(werte, i, e.target.value))}
              />
            </Field>
            <button
              type="button"
              className="knopf gefahr"
              onClick={() => entfernen(i)}
              aria-label={`${einzahl} ${i + 1} entfernen`}
            >
              <Trash2 size={14} /> Entfernen
            </button>
          </li>
        ))}
      </ul>

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={anhaengen}>
          <Plus size={14} /> {einzahl} hinzufügen
        </button>
        {zurueck && (
          <button type="button" className="knopf still" onClick={zurueckholen}>
            <Undo2 size={14} /> {beschriftung(zurueck.text)} zurückholen
          </button>
        )}
      </div>
    </section>
  )
}

/** Kurzfassung eines Eintrags für den Zurückholen-Knopf. */
function beschriftung(text: string): string {
  const sauber = text.trim()
  if (sauber === '') return 'Leeren Eintrag'
  return `„${sauber.length > 24 ? `${sauber.slice(0, 24)}…` : sauber}“`
}
